import { useEffect, useMemo, useRef, useState } from "react"
import { Table2, TrendingUp } from "lucide-react"
import { CHART_INK, SERIES } from "../../design/palette"
import { cx } from "../ui"
import { shortDay } from "../../lib/format"

/**
 * Outstanding backlog vs resolved, one point per day.
 *
 * Both lines are running *balances*, not running totals, so the right-hand end
 * of the chart always equals the stat tiles above it. Two series, so a legend is
 * always present and both lines are directly labelled at their last point -
 * identity is never carried by colour alone. A table view is one click away for
 * anyone who cannot read the plot.
 *
 * Series colours are validated against a white surface: CVD dE 10.3 (deutan),
 * normal-vision dE 22.6, both above 3:1 contrast. See design/palette.js.
 */

const PAD = { top: 16, right: 56, bottom: 26, left: 34 }
const HEIGHT = 220

function niceCeiling(value) {
	if (value <= 4) return Math.max(4, value)
	const magnitude = 10 ** Math.floor(Math.log10(value))
	return Math.ceil(value / (magnitude / 2)) * (magnitude / 2)
}

function useMeasuredWidth(fallback = 720) {
	const ref = useRef(null)
	const [width, setWidth] = useState(fallback)

	useEffect(() => {
		const node = ref.current
		if (!node || typeof ResizeObserver === "undefined") return undefined
		const observer = new ResizeObserver(([entry]) => {
			const next = entry.contentRect.width
			if (next > 0) setWidth(next)
		})
		observer.observe(node)
		return () => observer.disconnect()
	}, [])

	return [ref, width]
}

export function ProgressChart({ series = [], className, title = "Clarification progress" }) {
	const [hostRef, width] = useMeasuredWidth()
	const [hover, setHover] = useState(null)
	const [asTable, setAsTable] = useState(false)

	const model = useMemo(() => {
		if (!series.length) return null

		const peak = Math.max(1, ...series.map((p) => Math.max(p.open, p.closed)))
		const top = niceCeiling(peak)
		const plotWidth = Math.max(80, width - PAD.left - PAD.right)
		const plotHeight = HEIGHT - PAD.top - PAD.bottom
		const step = series.length > 1 ? plotWidth / (series.length - 1) : 0

		const x = (index) => PAD.left + index * step
		const y = (value) => PAD.top + plotHeight - (value / top) * plotHeight

		const line = (key) =>
			series.map((point, index) => `${index === 0 ? "M" : "L"}${x(index)},${y(point[key])}`).join(" ")

		const area = (key) =>
			`${line(key)} L${x(series.length - 1)},${PAD.top + plotHeight} L${x(0)},${
				PAD.top + plotHeight
			} Z`

		const ticks = []
		const tickStep = top <= 8 ? Math.max(1, Math.round(top / 4)) : top / 4
		for (let value = 0; value <= top + 0.001; value += tickStep) {
			ticks.push(Math.round(value))
		}

		return { top, plotWidth, plotHeight, step, x, y, line, area, ticks }
	}, [series, width])

	const last = series[series.length - 1]

	if (asTable) {
		return (
			<ChartFrame
				title={title}
				last={last}
				onToggleView={() => setAsTable(false)}
				viewLabel="Show chart"
				viewIcon={TrendingUp}
				className={className}
				hostRef={hostRef}
			>
				<div className="max-h-[220px] overflow-y-auto scrollbar-slim">
					<table className="w-full text-left text-[0.8125rem] tabular">
						<thead className="sticky top-0 bg-white">
							<tr className="border-b border-ink-200 text-2xs uppercase tracking-wide text-ink-500">
								<th className="py-1.5 pr-3 font-semibold">Date</th>
								<th className="py-1.5 pr-3 text-right font-semibold">Open</th>
								<th className="py-1.5 pr-3 text-right font-semibold">Resolved</th>
								<th className="py-1.5 text-right font-semibold">Movement</th>
							</tr>
						</thead>
						<tbody>
							{[...series].reverse().map((point) => (
								<tr key={point.date} className="border-b border-ink-100 last:border-0">
									<td className="py-1.5 pr-3 text-ink-600">{shortDay(point.date)}</td>
									<td className="py-1.5 pr-3 text-right font-medium text-ink-900">
										{point.open}
									</td>
									<td className="py-1.5 pr-3 text-right font-medium text-ink-900">
										{point.closed}
									</td>
									<td className="py-1.5 text-right text-ink-500">
										{point.opened_today || point.closed_today
											? [
													point.opened_today ? `+${point.opened_today} raised` : null,
													point.closed_today ? `${point.closed_today} resolved` : null
											  ]
													.filter(Boolean)
													.join(", ")
											: "-"}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</ChartFrame>
		)
	}

	return (
		<ChartFrame
			title={title}
			last={last}
			onToggleView={() => setAsTable(true)}
			viewLabel="Show as table"
			viewIcon={Table2}
			className={className}
			hostRef={hostRef}
		>
			{!model ? (
				<div className="grid h-[220px] place-items-center text-sm text-ink-400">
					Not enough history yet.
				</div>
			) : (
				<div className="relative">
					<svg
						width="100%"
						height={HEIGHT}
						viewBox={`0 0 ${width} ${HEIGHT}`}
						role="img"
						aria-label={`${title}: ${last?.open ?? 0} open and ${
							last?.closed ?? 0
						} resolved as of ${shortDay(last?.date)}`}
						onMouseLeave={() => setHover(null)}
						onMouseMove={(event) => {
							const box = event.currentTarget.getBoundingClientRect()
							const scale = width / box.width
							const local = (event.clientX - box.left) * scale - PAD.left
							const index = Math.round(local / (model.step || 1))
							setHover(Math.min(series.length - 1, Math.max(0, index)))
						}}
					>
						{/* Recessive grid: hairlines, no vertical rules. */}
						{model.ticks.map((tick) => (
							<g key={tick}>
								<line
									x1={PAD.left}
									x2={width - PAD.right}
									y1={model.y(tick)}
									y2={model.y(tick)}
									stroke={tick === 0 ? CHART_INK.axis : CHART_INK.grid}
									strokeWidth="1"
								/>
								<text
									x={PAD.left - 7}
									y={model.y(tick) + 3.5}
									textAnchor="end"
									fontSize="10"
									fill={CHART_INK.muted}
								>
									{tick}
								</text>
							</g>
						))}

						<path d={model.area("closed")} fill={SERIES.closed.fill} />
						<path d={model.area("open")} fill={SERIES.open.fill} />

						<path
							d={model.line("closed")}
							fill="none"
							stroke={SERIES.closed.stroke}
							strokeWidth="2"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
						<path
							d={model.line("open")}
							fill="none"
							stroke={SERIES.open.stroke}
							strokeWidth="2"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>

						{/* Direct labels at the live end - two series, so both get one. */}
						{last && (
							<>
								<EndLabel
									x={model.x(series.length - 1)}
									y={model.y(last.open)}
									value={last.open}
									colour={SERIES.open.stroke}
								/>
								<EndLabel
									x={model.x(series.length - 1)}
									y={model.y(last.closed)}
									value={last.closed}
									colour={SERIES.closed.stroke}
								/>
							</>
						)}

						{/* X axis: first, middle and last only - no label collisions. */}
						{[0, Math.floor((series.length - 1) / 2), series.length - 1].map((index, slot) => (
							<text
								key={`${index}-${slot}`}
								x={model.x(index)}
								y={HEIGHT - 8}
								textAnchor={slot === 0 ? "start" : slot === 2 ? "end" : "middle"}
								fontSize="10"
								fill={CHART_INK.muted}
							>
								{shortDay(series[index]?.date)}
							</text>
						))}

						{hover !== null && series[hover] && (
							<g>
								<line
									x1={model.x(hover)}
									x2={model.x(hover)}
									y1={PAD.top}
									y2={PAD.top + model.plotHeight}
									stroke={CHART_INK.axis}
									strokeWidth="1"
									strokeDasharray="3 3"
								/>
								{["closed", "open"].map((key) => (
									<circle
										key={key}
										cx={model.x(hover)}
										cy={model.y(series[hover][key])}
										r="4.5"
										fill={SERIES[key].stroke}
										stroke={CHART_INK.surface}
										strokeWidth="2"
									/>
								))}
							</g>
						)}
					</svg>

					{hover !== null && series[hover] && model && (
						<Tooltip
							point={series[hover]}
							left={model.x(hover)}
							width={width}
						/>
					)}
				</div>
			)}

			<Legend />
		</ChartFrame>
	)
}

function EndLabel({ x, y, value, colour }) {
	return (
		<text
			x={x + 8}
			y={y + 3.5}
			fontSize="11"
			fontWeight="600"
			fill={colour}
			style={{ fontVariantNumeric: "tabular-nums" }}
		>
			{value}
		</text>
	)
}

function Tooltip({ point, left, width }) {
	const flip = left > width * 0.62
	return (
		<div
			className="pointer-events-none absolute top-2 z-10 w-max rounded-lg border border-ink-200 bg-white px-2.5 py-1.5 shadow-lifted"
			style={
				flip
					? { right: `${width - left + 12}px` }
					: { left: `${left + 12}px` }
			}
		>
			<p className="text-2xs font-semibold text-ink-900">{shortDay(point.date)}</p>
			<dl className="mt-1 space-y-0.5">
				{["open", "closed"].map((key) => (
					<div key={key} className="flex items-center gap-1.5 text-2xs">
						<span
							className="h-1.5 w-1.5 rounded-full"
							style={{ background: SERIES[key].stroke }}
							aria-hidden
						/>
						<dt className="text-ink-500">{SERIES[key].label}</dt>
						<dd className="ml-auto pl-3 font-semibold tabular text-ink-900">
							{point[key]}
						</dd>
					</div>
				))}
			</dl>
			{(point.opened_today || point.closed_today) && (
				<p className="mt-1 border-t border-ink-100 pt-1 text-2xs text-ink-500">
					{[
						point.opened_today ? `+${point.opened_today} raised` : null,
						point.closed_today ? `${point.closed_today} resolved` : null
					]
						.filter(Boolean)
						.join(" - ")}
				</p>
			)}
		</div>
	)
}

function Legend() {
	return (
		<div className="mt-1 flex items-center gap-4 px-1">
			{["open", "closed"].map((key) => (
				<span key={key} className="inline-flex items-center gap-1.5 text-2xs text-ink-600">
					<span
						className="h-0.5 w-4 rounded-full"
						style={{ background: SERIES[key].stroke }}
						aria-hidden
					/>
					{SERIES[key].label}
				</span>
			))}
		</div>
	)
}

function ChartFrame({
	title,
	last,
	onToggleView,
	viewLabel,
	viewIcon: ViewIcon,
	className,
	hostRef,
	children
}) {
	return (
		<div ref={hostRef} className={cx("min-w-0", className)}>
			<div className="mb-2 flex items-start justify-between gap-3">
				<div>
					<h3 className="text-[0.9375rem] font-semibold text-ink-900">{title}</h3>
					<p className="mt-0.5 text-[0.8125rem] text-ink-500">
						{last
							? `${last.open} outstanding, ${last.closed} resolved as of ${shortDay(
									last.date
							  )}`
							: "Waiting on the first issue."}
					</p>
				</div>
				<button
					type="button"
					onClick={onToggleView}
					className="inline-flex shrink-0 items-center gap-1.5 rounded-lg px-2 py-1 text-2xs font-medium text-ink-500 transition-colors hover:bg-ink-100 hover:text-ink-900"
				>
					<ViewIcon size={13} aria-hidden />
					{viewLabel}
				</button>
			</div>
			{children}
		</div>
	)
}
